import { Incident } from '@src/main/modules/other_incidents/domain/incident.model';
import { useSendIncidentEmail } from '@src/main/modules/other_incidents/ui/hooks/useSendIncidentEmail.ts';
import { useSendToColumbus } from '@src/main/modules/other_incidents/ui/hooks/useSendToColumbus.ts';
import { useUpdateIncidentAnswered } from '@src/main/modules/other_incidents/ui/hooks/useUpdateIncidentAnswered';
import { useUpdateIncidentCategorization } from '@src/main/modules/other_incidents/ui/hooks/useUpdateIncidentCategorization.ts';
import { useUpdateIncidentStatus } from '@src/main/modules/other_incidents/ui/hooks/useUpdateIncidentStatus.ts';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useTranslation } from '@src/main/shared/ui/hooks/useTranslation';
import { useEffect, useState } from 'react';

export function useIncidentActions(
  incident: Incident | null,
  setFeedback: (value: { message: string; type: 'success' | 'error' | null }) => void,
  onIncidentUpdated?: (incident: Incident) => void,
) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const statusMap: Record<string, string> = {
    Nuevo: 'NUEVO',
    'En curso': 'EN_CURSO',
    'En espera': 'EN_ESPERA',
    Resuelto: 'RESUELTO',
    Cerrado: 'CERRADO',
    Cancelado: 'CANCELADO',
  };

  const { mutateAsync: updateCategorization } = useUpdateIncidentCategorization();
  const { mutateAsync: updateStatus } = useUpdateIncidentStatus();
  const { mutateAsync: sendEmailMutation } = useSendIncidentEmail();
  const { mutateAsync: sendToColumbusMutation } = useSendToColumbus();
  const { mutateAsync: updateIncidentAnswered } = useUpdateIncidentAnswered();

  const [emailText, setEmailText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => {
    if (!incident?.state) return;

    const map: Record<string, string> = {
      Nuevo: 'NUEVO',
      'En curso': 'EN_CURSO',
      'En espera': 'EN_ESPERA',
      Resuelto: 'RESUELTO',
      Cerrado: 'CERRADO',
      Cancelado: 'CANCELADO',
    };

    setSelectedStatus(map[incident.state] || '');
  }, [incident]);

  useEffect(() => {
    if (!incident) return;

    setFeedback({ message: '', type: null });
    setEmailText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id]);

  const handleError = (message: string) => {
    setFeedback({ type: 'error', message });
  };
  const handleSuccess = (message: string) => {
    setFeedback({ type: 'success', message });
  };

  const updateCategorizacion = async (categorization: string) => {
    if (!incident?.id) return;

    try {
      setIsSending(true);

      await updateCategorization({
        id: incident.id.toString(),
        categorization,
      });

      handleSuccess('Categorización actualizada correctamente');
    } catch {
      handleError('Error al actualizar la categorización');
    } finally {
      setIsSending(false);
    }
  };

  const sendEmail = async (plainText: string, file?: File | null) => {
    if (!incident) return;

    if (!emailText?.trim() && !plainText?.trim()) {
      handleError(t('otherIncidentManagement.userMensage.message'));
      return;
    }

    try {
      setIsSending(true);

      await sendEmailMutation({
        incident,
        plainText,
        htmlText: emailText,
        file,
      });

      handleSuccess(t('otherIncidentManagement.userMensage.email_ok'));

      setEmailText('');
      setUploadKey((prev) => prev + 1);

      await updateStatus({
        id: incident.id.toString(),
        status: statusMap[incident.state as string] || '',
      });
      await updateIncidentAnswered({
        id: incident.id.toString(),
        idAnswered: user?.employeeId || '',
        fullNameAnswered: user?.fullName || '',
        dateAnswered: new Date().toISOString(),
      });
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'EMPTY_MESSAGE') {
        handleError(t('otherIncidentManagement.userMensage.message'));
      } else {
        handleError(t('otherIncidentManagement.userMensage.email_fail'));
      }
    } finally {
      setIsSending(false);
    }
  };
  const sendToColumbus = async () => {
    if (!incident?.id) return;

    try {
      setIsSending(true);

      const updatedIncident = await sendToColumbusMutation(incident?.id);

      if (updatedIncident) {
        onIncidentUpdated?.(updatedIncident);
      }

      await updateStatus({
        id: incident.id.toString(),
        status: statusMap[incident.state as string] || '',
      });

      handleSuccess(t('otherIncidentManagement.userMensage.columbus_ok'));
    } catch {
      handleError(t('otherIncidentManagement.userMensage.columbus_fail'));
    } finally {
      setIsSending(false);
    }
  };

  return {
    emailText,
    setEmailText,
    selectedStatus,
    isSending,
    sendEmail,
    sendToColumbus,
    uploadKey,
    updateCategorizacion,
  };
}
